import {
  Avatar,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import { getAsyncStatusFromRtkQueryStates } from 'lib-shared-auth';
import { useEffect, useMemo } from 'react';
import { useUnmount } from 'react-use';
import isEmail from 'validator/lib/isEmail';

import { useLazyReadSearchInfiniteQuery } from 'apis/kraken';
import { appActions } from 'app/app.actions';
import { userModalActions } from 'components/usersModal';
import {
  CommandLauncherAction,
  CommandLauncherScope,
  useCommandLauncher,
} from 'utils/commandLauncher';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';

import { getCommandLauncherActionItems } from '../../utils/getCommandLauncherActionSelectionListItems';

/** Checks if the input looks like a phone number. */
const isPhoneNumberLike = (input: string): boolean => {
  // Remove common phone formatting characters and check if mostly digits
  const digits = input.replace(/[\s\-().+]/g, '');
  // At least 6 digits and mostly numeric
  return digits.length >= 6 && /^\d+$/.test(digits);
};

/** Represents the user scope list of the command launcher. */
export const UserScopeList = (): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const {
    actions: allActions,
    isActive,
    query,
    scope,
    scopeQuery,
    createAction,
    performAction,
    registerActions,
    setAsyncStatus,
  } = useCommandLauncher();
  const isInScope = scope === CommandLauncherScope.User;

  const [
    readSearchUsers,
    { results: users, isUninitialized, isFetching, isSuccess, isError },
  ] = useLazyReadSearchInfiniteQuery<'users'>();

  const actions = useMemo(
    () =>
      allActions.filter((action) =>
        action.scope?.includes(CommandLauncherScope.User),
      ),
    [allActions],
  );

  const handleSelectionListOnChange = (
    event: SelectionListChangeEvent<CommandLauncherAction>,
  ) => {
    if (event.selectedItem.isNode) {
      return;
    }

    performAction(event.selectedItem.value);
    dispatch(appActions.hideModal({ type: 'app:commandLauncher' }));
  };

  // Handles registering the static actions.
  useEffect(() => {
    // Prefill user fields based on input type
    // Use query.slice(1) to get everything after the @ scope prefix,
    // since scopeQuery uses split('@') which breaks email addresses
    const getPrefillUser = () => {
      const userQuery = query.slice(1).trim();
      if (!userQuery) return {};

      if (isEmail(userQuery)) {
        return { email: userQuery };
      }
      if (isPhoneNumberLike(userQuery)) {
        return { phone_number: userQuery };
      }
      return { name: userQuery };
    };

    const createContactAction = createAction({
      label: scopeQuery
        ? `Create new contact "${scopeQuery}"`
        : 'Create new contact',
      icon: 'user-plus',
      scope: [CommandLauncherScope.User],
      performFn: () => {
        dispatch(userModalActions.show({ user: getPrefillUser() }));
      },
    });

    const unregister = registerActions([createContactAction]);

    return () => unregister();
  }, [createAction, dispatch, query, registerActions, scopeQuery]);

  useEffect(() => {
    const userActions: readonly CommandLauncherAction[] = users.map((user) =>
      createAction({
        label: getUserDisplayName(user),
        prefix: <Avatar size="24" user={user} />,
        scope: [CommandLauncherScope.User],
        performFn: () =>
          navigate({
            path: '/contacts/user/:id',
            params: { path: { id: user.id } },
          }),
      }),
    );

    const unregister = registerActions(userActions);

    return () => unregister();
  }, [createAction, navigate, registerActions, users]);

  // Handles fetching users.
  useEffect(() => {
    if (isInScope && scopeQuery) {
      readSearchUsers(
        {
          scope: 'users',
          query: scopeQuery,
          pageSize: 50,
        },
        { debounceTime: 300 },
      );
    }
  }, [isInScope, readSearchUsers, scopeQuery]);

  useEffect(() => {
    setAsyncStatus(
      getAsyncStatusFromRtkQueryStates({
        isUninitialized,
        isFetching,
        isSuccess,
        isError,
      }),
    );
  }, [isError, isFetching, isSuccess, isUninitialized, setAsyncStatus]);

  useUnmount(() => setAsyncStatus('fulfilled'));

  if (!isInScope) {
    return <></>;
  }

  return (
    <SelectionList isActive={isActive} onChange={handleSelectionListOnChange}>
      {getCommandLauncherActionItems(actions)}
    </SelectionList>
  );
};
